import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { Box, ConfirmationModal, RadioButtonWithLabel, Typography } from 'seagull';
import { useLocaleKeys } from '~/i18n';
import { useDeleteProposal } from '~/pages/ProposalsDashboard/WithdrawModal/hooks/useDeleteProposal';
import { useDraftProposal } from '../../../hooks/reactQuery/mutations/useDraftProposal';
import { useArchiveProposal } from './hooks/useArchiveProposal';
import styles from './WithdrawModal.module.scss';
export const WithdrawModalTestIds = pathgen('WithdrawModalTestIds');
export var WithdrawModalAction = /*#__PURE__*/ function(WithdrawModalAction) {
    WithdrawModalAction[WithdrawModalAction["Archive"] = 0] = "Archive";
    WithdrawModalAction[WithdrawModalAction["Delete"] = 1] = "Delete";
    WithdrawModalAction[WithdrawModalAction["Draft"] = 2] = "Draft";
    return WithdrawModalAction;
}({});
export const WithdrawModal = (param)=>{
    let { relationshipId, draftId, onSuccess, dismissModal, openedBy } = param;
    const { t: texts } = useLocaleKeys('pages.proposalsDashboard.withdrawModal');
    const [action, setAction] = useState(0);
    const onSuccessAndDismiss = ()=>{
        onSuccess();
        dismissModal();
    };
    const { archive, isLoading: isArchiving } = useArchiveProposal({
        onSuccess: onSuccessAndDismiss,
        biCode: openedBy === 'agreement-studio' ? 18807 : 18804
    });
    const { draftProposal, isLoading: isDrafting } = useDraftProposal({
        onSuccess: onSuccessAndDismiss,
        withPageLoader: false,
        biCode: openedBy === 'agreement-studio' ? 18808 : 18805
    });
    const { deleteProposal, isLoading: isDeleting } = useDeleteProposal({
        onSuccess: onSuccessAndDismiss,
        biCode: openedBy === 'agreement-studio' ? 18809 : 18806
    });
    const isLoading = isArchiving || isDrafting || isDeleting;
    const primaryButton = useMemo(()=>{
        switch(action){
            case 0:
                return {
                    label: texts.archiveProposal(),
                    onClick: ()=>{
                        return archive({
                            draftId,
                            relationshipId
                        });
                    },
                    disabled: false,
                    testId: WithdrawModalTestIds.ArchiveConfirmButton()
                };
            case 2:
                return {
                    label: texts.moveToDrafts(),
                    onClick: ()=>{
                        return draftProposal({
                            draftId,
                            relationshipId
                        });
                    },
                    disabled: false,
                    testId: WithdrawModalTestIds.DraftConfirmButton()
                };
            case 1:
                return {
                    label: texts.permanentlyDelete(),
                    onClick: ()=>{
                        return deleteProposal(relationshipId);
                    },
                    disabled: false,
                    testId: WithdrawModalTestIds.DeleteConfirmButton()
                };
        }
    }, [
        action
    ]);
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        skin: "danger",
        width: "590px",
        title: texts.title(),
        testId: WithdrawModalTestIds.Root(),
        primaryButtonTestId: primaryButton.testId,
        primaryButtonLabel: primaryButton.label,
        onPrimaryButtonClick: primaryButton.onClick,
        primaryButtonLoading: isLoading,
        secondaryButtonTestId: WithdrawModalTestIds.SecondaryCloseButton(),
        textAlignCenter: false,
        onCloseButtonClick: dismissModal,
        secondaryButtonLabel: texts.cancel(),
        onSecondaryButtonClick: dismissModal,
        content: /*#__PURE__*/ _jsxs(Box, {
            className: styles.root,
            children: [
                /*#__PURE__*/ _jsx(Box, {
                    className: styles.header,
                    children: /*#__PURE__*/ _jsx(Typography, {
                        children: texts.withdrawDescription()
                    })
                }),
                /*#__PURE__*/ _jsxs(Box, {
                    className: styles.content,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            children: texts.chooseAction()
                        }),
                        /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                            label: texts.archiveLabel(),
                            testId: WithdrawModalTestIds.RadioOptions.Archive(),
                            checked: action === 0,
                            onChange: ()=>setAction(0)
                        }),
                        /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                            label: texts.moveBackToDraftsLabel(),
                            testId: WithdrawModalTestIds.RadioOptions.Draft(),
                            checked: action === 2,
                            onChange: ()=>setAction(2)
                        }),
                        /*#__PURE__*/ _jsx(RadioButtonWithLabel, {
                            label: texts.deleteLabel(),
                            testId: WithdrawModalTestIds.RadioOptions.Delete(),
                            checked: action === 1,
                            onChange: ()=>setAction(1)
                        })
                    ]
                })
            ]
        })
    });
};
